import { Alert, Col, Empty, Row, Spin, Typography } from 'antd';
import { useWisecureQuery } from '@wisecure/api-client/hooks';
import type { SecurityCatalogProduct, SecurityCatalogResponse } from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import { WisecureCard } from '@/components/base';
import { neutral } from '@/theme';
import { PRODUCT_LABELS } from '@/components/security/labels';
import { CatalogItemsTable } from '@/components/security/CatalogItemsTable';
import { PricingSettingsCard } from '@/components/security/PricingSettingsCard';
import { EstimateSimulatorCard } from '@/components/security/EstimateSimulatorCard';

const { Text } = Typography;

const PRODUCTS: SecurityCatalogProduct[] = ['ALARM', 'VIDEO', 'OPTION'];

const PRODUCT_HINTS: Record<SecurityCatalogProduct, string> = {
  ALARM: "Un prix d'achat vide retire l'équipement de la formule.",
  VIDEO: "Un prix d'achat vide retire l'équipement de la formule.",
  OPTION: 'Les options ont le même prix dans toutes les formules (prix Essentiel).',
};

export function SecurityCatalogPage() {
  const { data, isLoading, isFetching } = useWisecureQuery<SecurityCatalogResponse>(
    queryKeys.securityCatalog,
    (client) => client.dashboardSecurity.securityDashboardControllerGetCatalog(),
  );

  return (
    <div>
      <PageHeader
        title="Catalogue sécurité"
        subtitle="Équipements et tarifs du configurateur de l'app client"
        extra={isFetching && !isLoading ? <Spin size="small" /> : undefined}
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="Comment le prix est calculé"
        description={
          <div>
            <div>
              Prix de vente = prix d'achat HT × (1 + marge), arrondi aux 5 €. Chaque champ s'enregistre en
              quittant la case (ou avec Entrée) et le prix de vente est recalculé.
            </div>
            <div>
              Prix installé = matériel + fournitures + main d'œuvre (temps de pose + mise en service, arrondi à
              la demi-journée avec une tolérance, × taux horaire) + forfait mise en service, puis + provision
              aléas, arrondi aux 10 €.
            </div>
          </div>
        }
      />

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      ) : !data ? (
        <Empty description="Catalogue indisponible" />
      ) : (
        <>
          {PRODUCTS.map((product) => {
            const items = data.items.filter((item) => item.product === product);
            return (
              <WisecureCard
                key={product}
                title={PRODUCT_LABELS[product]}
                extra={<Text style={{ color: neutral[500], fontSize: 12 }}>{PRODUCT_HINTS[product]}</Text>}
                style={{ marginBottom: 24 }}
              >
                <CatalogItemsTable items={items} singlePlan={product === 'OPTION'} />
              </WisecureCard>
            );
          })}

          <Row gutter={24}>
            <Col xs={24} xl={12}>
              <PricingSettingsCard key={data.settings.updatedAt} settings={data.settings} />
            </Col>
            <Col xs={24} xl={12}>
              <EstimateSimulatorCard />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
